/** A probe older than this says nothing about the printer right now. The
 *  probe cron runs every 15 min, so 30 min means it missed at least one run. */
export const CONNECTION_STALE_MS = 30 * 60_000

export type PrinterReachability = 'online' | 'offline' | 'unknown'

/**
 * Offline only when the last probe said "not connected" AND is recent. A
 * missing/stale/unparseable probe is `unknown` (we show nothing rather than
 * a false alarm); a recent "connected" probe is `online`.
 */
export function printerReachability(
  epsonConnected: boolean | null | undefined,
  checkedAt: string | null | undefined,
  now: number = Date.now(),
): PrinterReachability {
  if (epsonConnected == null || !checkedAt) return 'unknown'
  const age = now - new Date(checkedAt).getTime()
  if (Number.isNaN(age) || age > CONNECTION_STALE_MS) return 'unknown'
  return epsonConnected ? 'online' : 'offline'
}

interface ProbedPrinter {
  epsonConnected: boolean | null
  connectionCheckedAt: string | null
  lastSeenConnectedAt: string | null
}

/**
 * For screens without a chosen printer (/my/print): the printer to warn about
 * when EVERY printer is confirmed offline (the most recently seen one), else
 * null. If any printer is online or unknown, the release picker's badges say
 * which ones to avoid, so no page-level warning.
 */
export function allPrintersOffline<P extends ProbedPrinter>(
  printers: P[],
  now: number = Date.now(),
): P | null {
  if (printers.length === 0) return null
  if (
    printers.some(
      (p) => printerReachability(p.epsonConnected, p.connectionCheckedAt, now) !== 'offline',
    )
  ) {
    return null
  }
  return printers.reduce((a, b) =>
    (b.lastSeenConnectedAt ?? '') > (a.lastSeenConnectedAt ?? '') ? b : a,
  )
}
